import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
import { formatPrice } from "../../utils/functions";

const SellerPlans = () => {
  const { user } = useAuth();
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const [packageHistory, setPackageHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Load initial data
  useEffect(() => {
    if (user?.seller?.id) {
      fetchSellerPackageHistory();
    }
  }, [user?.seller?.id]);

  const fetchSellerPackageHistory = async () => {
    if (!user?.seller?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get("/sellerpackagehistories/paginated", {
        params: {
          page: 1,
          limit: 50,
          orderBy: "id",
          order: "DESC",
          search: "",
          slug: "",
          sellerId: user?.seller?.id || user?.sellerId,
        },
      });

      console.log("Package history response:", response.data);

      // Handle different possible response structures
      let data = response.data;
      if (data && Array.isArray(data)) {
        setPackageHistory(data);
      }

      // else if (data && Array.isArray(data.data)) {
      //   setPackageHistory(data.data);
      // }
      // wednesday fix - to show only active package  in seller profile page
      else if (data && Array.isArray(data.data)) {
        const activePackages = data.data.filter((pkg) => pkg.isActive);

        const latestActive = activePackages.sort(
          (a, b) => new Date(b.approvedAt) - new Date(a.approvedAt),
        )[0];

        setPackageHistory(latestActive ? [latestActive] : []);
      }
      // ============================ e===========
      else if (data && Array.isArray(data.items)) {
        setPackageHistory(data.items);
      } else {
        console.log("Unexpected response structure:", data);
        setPackageHistory([]);
      }
    } catch (err) {
      console.error("Error fetching seller package history:", err);
      setError(translate("sellerPlans.messages.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
    );
  };

  const getStatusBadge = (isActive, isApproved) => {
    if (isActive && isApproved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-700 text-white">
          {translate("sellerPlans.status.active")}
        </span>
      );
    } else if (isApproved && !isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-700 text-white">
          {translate("sellerPlans.status.inactive")}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-700 text-white">
          {translate("sellerPlans.status.pending")}
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow-md rounded-[12px] p-6">
        <div className="text-center text-red-600">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-[#FFF9F9] rounded-lg shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      <div className="bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1">
        <div className="p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2">
          <h2 className="text-base sm:text-lg font-semibold">
            {translate("sellerPlans.heading")}
          </h2>
        </div>
      </div>

      {/* Remove tabs section since we only have one view now */}

      <div className="px-5">
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 my-4 text-green-700">
            {successMessage}
          </div>
        )}
        {/* Show only My Packages view */}
        {!Array.isArray(packageHistory) || packageHistory.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {translate("sellerPlans.messages.noPackageHistory")}
            </h3>
            <p className="text-gray-500">
              {translate("sellerPlans.messages.noPackageHistoryYet")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-6">
            {packageHistory.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
              >
                {/* Card Header */}
                <div className="relative p-6">
                  {/* Package Name */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {pkg.sellerPackage?.name}
                  </h3>

                  {/* Location */}
                  <div
                    className={`flex items-center text-gray-600 mb-4 ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <svg
                      className={`w-4 h-4 text-red-500 ${
                        isRTL ? "ml-2" : "mr-2"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm">
                      {translate("sellerPlans.labels.location")}
                    </span>
                  </div>

                  {/* Badge */}
                  <div
                    className={`absolute top-4 ${isRTL ? "left-4" : "right-4"}`}
                  >
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${
                        pkg.sellerPackage?.name?.includes("Corporate")
                          ? "bg-green-500"
                          : "bg-blue-500"
                      }`}
                    >
                      {pkg.sellerPackage?.name?.includes("Corporate")
                        ? translate("sellerPlans.labels.corporate")
                        : translate("sellerPlans.labels.individual")}
                    </span>
                  </div>
                </div>

                {/* Features Section */}
                <div className="px-6 pb-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {translate("sellerPlans.labels.features")}
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {translate("sellerPlans.labels.includedIn")}{" "}
                    {pkg.sellerPackage?.name?.includes("Corporate")
                      ? translate("sellerPlans.labels.corporate")
                      : translate("sellerPlans.labels.individual")}{" "}
                    {translate("sellerPlans.labels.plan")}
                  </p>

                  <div className="space-y-2">
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">
                        {translate("sellerPlans.labels.listingFee")}{" "}
                        {formatPrice(pkg.sellerPackage?.listingFee || 0)}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">
                        {translate("sellerPlans.labels.relistingFee")}{" "}
                        {formatPrice(pkg.sellerPackage?.reListingFee || 0)}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">
                        {translate("sellerPlans.labels.numberOfRuns")}{" "}
                        {pkg.sellerPackage?.numberOfRuns || 0}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">
                        {translate(
                          "sellerPlans.labels.fineAfterFreeParkingDays",
                        )}{" "}
                        {formatPrice(
                          pkg.sellerPackage?.fineAfterFreeParkingDays || 0,
                        )}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">
                        {translate("sellerPlans.labels.freeParkingDays")}{" "}
                        {pkg.sellerPackage?.freeParkingDays || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Fee Range Table */}
                <div className="px-6 pb-4">
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            className={`px-4 py-3 ${
                              isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                          >
                            {translate("sellerPlans.labels.startingRange")}
                          </th>
                          <th
                            className={`px-4 py-3 ${
                              isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                          >
                            {translate("sellerPlans.labels.toRange")}
                          </th>
                          <th
                            className={`px-4 py-3 ${
                              isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                          >
                            {translate("sellerPlans.labels.fee")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pkg.sellerPackage?.sellerPackageRanges?.map(
                          (range, index) => (
                            <tr
                              key={range.id || index}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-4 py-3 text-sm text-gray-900 bg-pink-50">
                                {formatPrice(range.startRange)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 bg-pink-50">
                                {formatPrice(range.toRange)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 bg-pink-50">
                                {formatPrice(range.fee)}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Status Information */}
                <div className="bg-gray-50 px-6 py-3 border-t">
                  <div
                    className={`flex justify-between items-center ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <div className="text-xs text-gray-500">
                      {translate("sellerPlans.labels.status")}{" "}
                      {getStatusBadge(pkg.isActive, pkg.isApproved)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {translate("sellerPlans.labels.approved")}{" "}
                      {formatDate(pkg.approvedAt)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerPlans;
